import React, { useEffect, useState } from 'react';
import { Pencil, X } from 'lucide-react';
import { domiciliaryScheduleService } from '../../../services';
import { Modal, Button } from '../../../components';
import { toast } from 'react-hot-toast';

// Shared Assign popup (Care Packages + Schedule). Shows the visit context, the
// current assignment, and a server-ranked + badged recommended-carer list.
// `visit` carries the clicked visit; null = closed.
const BADGE_STYLES = {
  'KNOWN CLIENT': 'bg-indigo-50 text-indigo-600',
  PREFERRED: 'bg-amber-50 text-amber-600',
  'ZONE MATCH': 'bg-blue-50 text-blue-600',
  COVER: 'bg-emerald-50 text-emerald-600',
  'AVAILABILITY MATCH': 'bg-sky-50 text-sky-600',
};

const Card = ({ label, children }) => (
  <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50/60 dark:bg-[#20242c] px-3 py-2">
    <div className="text-[9px] font-bold uppercase tracking-wide text-gray-400 dark:text-gray-500">{label}</div>
    <div className="mt-1 text-[12px] font-semibold text-gray-700 dark:text-gray-200">{children}</div>
  </div>
);

// `stageAssign(carers, scope)` (optional) — when provided (Schedule screen),
// the assignment is queued as a draft change instead of written immediately.
// Without it (Care Packages screen), it writes through `onChanged` as before.
// `lockAlways` (Care Packages screen): assigning a package always sets the
// standing carer for EVERY day (decided by the package's start/end + pattern),
// never a single date — so the per-day scope toggle is hidden.
const AssignPackageModal = ({ visit, date, onClose, onChanged, onEditPackage, stageAssign, lockAlways }) => {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [current, setCurrent] = useState([]); // [{id,name,initials}]
  const [busy, setBusy] = useState(false);
  const [scope, setScope] = useState('always');

  // Tolerant of both shapes: Care Packages sends `client` + `carers`; the
  // Schedule sends `resident` and (optionally) no carers.
  const client = (visit && (visit.client || visit.resident)) || {};

  useEffect(() => {
    if (!visit) return;
    setCurrent(visit.carers || []);
    setScope(lockAlways ? 'always' : ((visit.carers || []).length === 0 ? 'always' : 'today'));
    setLoading(true);
    domiciliaryScheduleService.getAssignableCarers(visit.assignment_id, date)
      .then((res) => { if (res.success) setInfo(res.data); })
      .catch(() => toast.error('Failed to load carers'))
      .finally(() => setLoading(false));
  }, [visit, date]);

  if (!visit) return null;

  const maxCarers = visit.double_up ? 2 : 1;

  // Commit an assignment change. On the Schedule screen this stages a draft
  // (no API call); on the Care Packages screen it writes immediately.
  const commit = async (nextCarers) => {
    if (stageAssign) {
      stageAssign(nextCarers, scope);
      toast.success('Assignment added to draft');
      return;
    }
    setBusy(true);
    try {
      const res = await domiciliaryScheduleService.assignCarer({
        assignment_id: visit.assignment_id, date, carer_ids: nextCarers.map((c) => c.id), scope,
      });
      if (res.success) { toast.success(res.message || 'Updated'); onChanged && onChanged(); }
    } catch (e) {
      toast.error(e.response?.data?.message || 'Error assigning carer');
    } finally {
      setBusy(false);
    }
  };

  const assign = (carer) => {
    let next;
    if (current.some((c) => c.id === carer.id)) return;
    if (maxCarers === 1) next = [{ id: carer.id, name: carer.name, initials: carer.initials }];
    else {
      if (current.length >= 2) { toast.error('Max 2 carers for a double-up visit'); return; }
      next = [...current, { id: carer.id, name: carer.name, initials: carer.initials }];
    }
    setCurrent(next);
    commit(next);
  };

  const remove = (id) => {
    const next = current.filter((c) => c.id !== id);
    setCurrent(next);
    commit(next);
  };

  return (
    <Modal isOpen onClose={onClose} hideHeader bodyClassName="flex flex-col flex-1 min-h-0" maxWidth="max-w-3xl">
        {/* Header */}
        <div className="flex items-start justify-between border-b border-gray-100 dark:border-gray-800 px-6 py-4">
          <div>
            <h2 className="text-[17px] font-semibold text-gray-800 dark:text-gray-200">{visit.package_name}</h2>
            <p className="text-xs text-gray-400 dark:text-gray-500">
              {client.name} · {date}{info?.zone_name ? ` · ${info.zone_name}` : ''}
            </p>
          </div>
          <button onClick={onClose} className="grid h-6 w-6 place-items-center rounded-full bg-blue-600 text-white hover:bg-blue-700">
            <X className="h-3.5 w-3.5" strokeWidth={3} />
          </button>
        </div>

        {/* Info cards */}
        <div className="grid grid-cols-2 gap-2 px-6 py-4 sm:grid-cols-4">
          <Card label="Visit">{visit.visit_type || 'Visit'} · {visit.start}{visit.end ? `–${visit.end}` : ''}</Card>
          <Card label="Service-user address">{client.address || '—'}{client.postcode ? ` · ${client.postcode}` : ''}</Card>
          <Card label="Travel zone">{info?.zone_name || 'Unzoned'}</Card>
          <Card label="Travel area">{client.postcode || '—'}</Card>
        </div>

        {/* Body */}
        <div className="grid flex-1 grid-cols-1 gap-4 overflow-y-auto px-6 pb-4 md:grid-cols-2">
          {/* Current assignment */}
          <div className="rounded-xl border border-gray-200 dark:border-gray-800 p-4">
            <h3 className="mb-3 text-[10px] font-bold uppercase tracking-wide text-gray-400 dark:text-gray-500">Current assignment</h3>
            {current.length === 0 ? (
              <div className="flex items-center gap-3 rounded-lg bg-rose-50/60 p-3">
                <span className="h-9 w-9 rounded-full bg-rose-100" />
                <div>
                  <div className="text-sm font-semibold text-rose-600">No carer assigned</div>
                  <div className="text-[11px] text-gray-400 dark:text-gray-500">Choose a recommended carer.</div>
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                {current.map((c) => (
                  <div key={c.id} className="flex items-center gap-3 rounded-lg border border-gray-200 dark:border-gray-800 p-2.5">
                    <span className="grid h-9 w-9 place-items-center rounded-full bg-blue-100 text-xs font-bold text-blue-700">{c.initials}</span>
                    <span className="flex-1 text-sm font-semibold text-gray-800 dark:text-gray-200">{c.name}</span>
                    <button onClick={() => remove(c.id)} className="text-gray-400 dark:text-gray-500 hover:text-rose-500"><X className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>
            )}

            {/* Scope — hidden on the Care Packages screen, where assigning is
                always package-wide (every day). */}
            {lockAlways ? (
              <div className="mt-4 rounded-lg bg-blue-50/70 px-3 py-2 text-[11px] font-semibold text-blue-700">
                Assigned for every day of this package (its start–end dates).
              </div>
            ) : (
              <div className="mt-4">
                <div className="mb-1 text-[10px] font-bold uppercase tracking-wide text-gray-400 dark:text-gray-500">Apply to</div>
                <div className="flex rounded-lg border border-gray-200 dark:border-gray-700 p-1 text-xs font-semibold">
                  <button onClick={() => setScope('today')} className={`flex-1 rounded-md px-3 py-1.5 ${scope === 'today' ? 'bg-blue-600 text-white' : 'text-gray-500 dark:text-gray-400'}`}>This visit</button>
                  <button onClick={() => setScope('always')} className={`flex-1 rounded-md px-3 py-1.5 ${scope === 'always' ? 'bg-blue-600 text-white' : 'text-gray-500 dark:text-gray-400'}`}>Every day</button>
                </div>
              </div>
            )}
          </div>

          {/* Recommended carers */}
          <div>
            <h3 className="mb-3 text-[10px] font-bold uppercase tracking-wide text-gray-400 dark:text-gray-500">Recommended carers</h3>
            {loading ? (
              <div className="py-8 text-center text-sm text-gray-400 dark:text-gray-500">Loading…</div>
            ) : (
              <div className="flex max-h-[42vh] flex-col gap-2 overflow-y-auto pr-1">
                {(info?.carers || []).map((c) => {
                  const isCurrent = current.some((x) => x.id === c.id);
                  return (
                    <div key={c.id} className="flex items-center gap-3 rounded-xl border border-gray-200 dark:border-gray-800 p-2.5">
                      <span className="grid h-9 w-9 flex-shrink-0 place-items-center rounded-full bg-blue-100 text-xs font-bold text-blue-700">{c.initials}</span>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-semibold text-gray-800 dark:text-gray-200">{c.name}</div>
                        <div className="truncate text-[11px] text-gray-400 dark:text-gray-500">{c.status}</div>
                        {c.conflict && (
                          <div className="mt-0.5 truncate text-[10px] font-semibold text-amber-600">
                            ⚠ Already on {c.conflict.package_name} {c.conflict.time}
                          </div>
                        )}
                        <div className="mt-1 flex flex-wrap gap-1">
                          {c.badges.map((b) => (
                            <span key={b} className={`rounded px-1.5 py-0.5 text-[8px] font-bold uppercase ${BADGE_STYLES[b] || 'bg-gray-100 text-gray-500'}`}>{b}</span>
                          ))}
                        </div>
                      </div>
                      <button
                        onClick={() => assign(c)}
                        disabled={busy || isCurrent || !c.assignable}
                        className={`h-8 rounded-lg border px-3 text-[11px] font-bold transition ${
                          isCurrent ? 'border-emerald-200 bg-emerald-50 text-emerald-600'
                            : c.assignable ? 'border-blue-200 text-blue-600 hover:bg-blue-50'
                            : 'border-gray-200 text-gray-300'
                        }`}
                      >
                        {isCurrent ? 'Assigned' : 'Assign'}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-gray-100 dark:border-gray-800 px-6 py-4">
          {onEditPackage ? (
            <Button variant="secondary" leftIcon={<Pencil className="h-4 w-4" />} onClick={() => onEditPackage(visit.assignment_id)}>
              Edit package
            </Button>
          ) : <span />}
          <Button variant="secondary" onClick={onClose}>Done</Button>
        </div>
    </Modal>
  );
};

export default AssignPackageModal;
